import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_BASE_URL } from './tokens';
import { Machine, MIFRequest, PurgeDeviceQueueResponse } from 'src/utils/models/machine.model';
import { BackendResponse } from '@schenck/rotec-components';

@Injectable()
export class MachineApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  private readonly machineUrl: string;

  constructor() {
    const baseUrl = this.baseUrl;

    this.machineUrl = baseUrl + '/devices';
  }

  public get$(): Observable<Machine[]> {
    return this.http.get<Machine[]>(this.machineUrl);
  }

  public getMachineById$(id: string): Observable<Machine> {
    return this.http.get<Machine>(`${this.machineUrl}/${id}`);
  }

  public delete$(id: string): Observable<boolean> {
    return this.http.delete(`${this.machineUrl}/${id}`).pipe(map((result: boolean) => result));
  }

  public create$(user: Partial<Machine>): Observable<Machine> {
    return this.http.post<Machine>(this.machineUrl, user);
  }

  public update$(machine: Machine): Observable<Machine> {
    return this.http.put<Machine>(`${this.machineUrl}/${machine.id}`, machine);
  }

  public exportMachinesAsCSV$(): Observable<Blob> {
    return this.http.get(`${this.machineUrl}/export/csv`, {
      responseType: 'blob',
    });
  }

  public registerMIF$(data: MIFRequest): Observable<BackendResponse.Response<Machine>> {
    const formData: FormData = new FormData();
    formData.append('Content', data.mif, data.mif.name);
    formData.append('Name', data.machineName);
    formData.append('TenantId', data.tenantId);
    formData.append('License', data.softwareLicense);
    formData.append('Version', data.softwareVersion);

    return this.http
      .post<Machine>(`${this.machineUrl}/import/mif`, formData)
      .pipe(BackendResponse.mapToResponse());
  }

  public purgeMessageQueue$(deviceId: string): Observable<PurgeDeviceQueueResponse> {
    return this.http.delete<PurgeDeviceQueueResponse>(`${this.machineUrl}/${deviceId}/purge`).pipe(
      catchError((err: HttpErrorResponse) => {
        const message = err?.error ? err.error.message : err?.message;
        throw message;
      })
    );
  }

  /**
   * Check if password files exist
   * @param deviceId
   * @returns
   * - 200 when password files exist<br>
   * - 204 when request was successful but no password files exist<br>
   * - 5xx when something went wrong
   */
  public getExistingPasswordFile$(deviceId: string): Observable<any> {
    return this.http.get(`${this.machineUrl}/${deviceId}/files/mas/passwordexists`, {
      observe: 'response',
    });
  }

  /**
   * Create/Overwrite Password File
   * @param deviceId
   * @returns
   * - 201 when password file was created
   * - 400 when not all required passwords were set
   * - 404 when device was not found
   * - 5xx when something went wrong
   */
  public sendPasswordFile$(deviceId: string): Observable<any> {
    return this.http.post(`${this.machineUrl}/${deviceId}/files/mas/password`, null, {
      observe: 'response',
    });
  }
}
